import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import Swal from 'sweetalert2';
import api from '@/services/api';
import authService from '@/services/authService';
import { clearPosOfflineData, countUnsyncedPendingSales } from '@/hooks/usePosOffline';
import type { User, Notifs, AuthState, LoginCredentials, ResetPasswordData, AccessData, LoginResponse, UserAuthData, TwoFactorVerifyData } from '@/types/auth';

export type { User, Notifs, AuthState, LoginCredentials, ResetPasswordData, AccessData, LoginResponse, UserAuthData, TwoFactorVerifyData };

/**
 * The axios response interceptor rejects with the camelCased server body plus an
 * attached `status` (services/api.ts) — NOT an Error. Reading `err.message`
 * alone therefore discarded the server's message ("Invalid credentials",
 * "Account suspended", a 422 field-error map) and always showed the generic
 * fallback. Prefer field errors, then the body message, then an Error.
 */
function extractAuthErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const body = err as { message?: unknown; errors?: Record<string, unknown> };
    if (body.errors && typeof body.errors === 'object') {
      for (const value of Object.values(body.errors)) {
        const messages = Array.isArray(value) ? value : [value];
        const first = messages.find(
          (m): m is string => typeof m === 'string' && m.trim().length > 0,
        );
        if (first) return first;
      }
    }
    if (typeof body.message === 'string' && body.message.trim().length > 0) {
      return body.message;
    }
  }
  if (err instanceof Error && err.message.trim().length > 0) {
    return err.message;
  }
  return fallback;
}

/**
 * HTTP status for a rejection, tolerant of BOTH interceptor shapes: the
 * camelCased body with `status` attached (the common path) and a raw AxiosError
 * (`.response.status`, e.g. the admin-session 403 passthrough). Reading only
 * `.response.status` made the 401/403 branches below permanently unreachable —
 * every failure was classified as transient.
 */
function rejectedStatus(err: unknown): number | undefined {
  const e = err as { response?: { status?: number }; status?: number };
  return e?.response?.status ?? e?.status;
}

const initialState: AuthState = {
  isAuthenticated: false,
  Permissions: null,
  currentUserPermissions: null,
  allmodules: null,
  user: {},
  currentUser: {},
  loading: false,
  error: null,
  notifs: {
    quantityAlerts: 0,
    batchAlerts: 0,
  },
  defaultLanguage: 'en',
  showLanguage: 1,
  isAdminLoggedIn: false,
  isSuperAdmin: false,
  tenantId: null,
  accessToken: null,
  telemetryOptIn: true,
};

export const login = createAsyncThunk<AccessData, LoginCredentials, { rejectValue: string }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      // Pending 2FA: posToken is a short-lived provisional token. It is kept
      // so the challenge request can authenticate, but the session is not
      // established until verifyTwoFactor succeeds.
      const tokenData: AccessData = {
        ...response,
        accessToken: response.posToken,
        twoFactorRequired: !!response.twoFactorRequired,
      };
      return tokenData;
    } catch (err: unknown) {
      return rejectWithValue(extractAuthErrorMessage(err, 'Login failed'));
    }
  }
);

export const verifyTwoFactor = createAsyncThunk<AccessData, TwoFactorVerifyData, { rejectValue: string }>(
  'auth/verifyTwoFactor',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.verifyTwoFactor(data);
      const tokenData: AccessData = {
        ...response,
        accessToken: response.posToken,
        twoFactorRequired: false,
      };
      return tokenData;
    } catch (err: unknown) {
      return rejectWithValue(extractAuthErrorMessage(err, 'Two-factor verification failed'));
    }
  }
);

export const forgotPassword = createAsyncThunk<void, string, { rejectValue: string }>(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      await authService.forgotPassword(email);
    } catch (err: unknown) {
      return rejectWithValue(extractAuthErrorMessage(err, 'Password reset request failed'));
    }
  }
);

export const resetPassword = createAsyncThunk<void, ResetPasswordData, { rejectValue: string }>(
  'auth/resetPassword',
  async (data, { rejectWithValue }) => {
    try {
      await authService.resetPassword(data);
    } catch (err: unknown) {
      return rejectWithValue(extractAuthErrorMessage(err, 'Password reset failed'));
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    const excludedPaths = ['/login', '/register', '/forgot', '/reset-password', '/reset_password'];
    const currentPath = window.location.pathname;
    if (currentPath && !excludedPaths.some(p => currentPath.includes(p))) {
      localStorage.setItem('last_visited_page', JSON.stringify(currentPath));
    }

    // An unsynced offline sale is money already collected but never booked —
    // clearing the offline store on logout destroys it permanently. Make the
    // cashier confirm (they should instead reconnect and Sync Now).
    // i18n is imported DYNAMICALLY: '@/i18n' runs i18next.init() as an
    // import side effect, and the vitest single-fork runner shares module
    // memory across test files — a static import here initialized real
    // translations process-wide and broke key-based characterization tests.
    // The wipe below clears the WHOLE profile DB (all users), so count ALL
    // unsynced rows here — a per-user count would understate the loss when
    // another cashier's sales are still queued on this terminal.
    // An IndexedDB read can reject (unavailable / quota). A failure here must
    // not abort the logout before the local auth state is cleared, or the user
    // is stranded half-logged-out and cannot reach /login. Treat an unknown
    // count as zero and proceed; the wipe below still clears the store.
    let pending = 0;
    try {
      pending = await countUnsyncedPendingSales();
    } catch {
      pending = 0;
    }
    if (pending > 0) {
      const { default: i18n } = await import('@/i18n');
      const { isConfirmed } = await Swal.fire({
        title: i18n.t('Offline_logout_pending_title'),
        text: i18n.t('Offline_logout_pending_text', { count: pending }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: i18n.t('Offline_logout_confirm'),
        cancelButtonText: i18n.t('Offline_logout_cancel'),
        confirmButtonColor: '#d33',
      });
      if (!isConfirmed) return;
    }

    try {
      await api({ method: 'post', url: '/logout' });
    } catch {
      // TODO: use a centralized error logger instead of console.error
    } finally {
      // Offline stores are per browser profile — wipe them so the next login
      // (possibly a different user/tenant) cannot replay this user's queued
      // offline sales or read its cached catalog.
      //
      // AWAIT it: indexedDB.deleteDatabase is asynchronous, and navigating
      // immediately aborted it mid-flight (the request fires `blocked` and the
      // database survives), leaving the previous user's queued sales behind.
      await clearPosOfflineData();
      // posSession is whitelisted for reload survival, but must never leak
      // across cashiers: reset the in-memory cart and drop the persisted slice
      // so the next login starts with an empty ticket.
      const { resetCart } = await import('./posSessionSlice');
      dispatch(resetCart());
      try {
        // Two redux-persist stores exist: the main app store (default key
        // 'root') and the login entry point's lightweight store (key
        // 'auth-root', which whitelists `auth` incl. accessToken and user
        // PII). Both must be dropped or a valid session survives logout.
        localStorage.removeItem('persist:root');
        localStorage.removeItem('persist:auth-root');
      } catch {
        // ignore storage errors on logout
      }
      dispatch(clearAuthData());
      window.location.href = '/login';
    }
  }
);

/**
 * Admin-scoped logout. Hits `/admin/auth/logout` to invalidate the
 * super-admin session, then clears local auth state. Distinct from the
 * regular logout thunk which invalidates the tenant-user session.
 *
 * Same unsynced-offline-sales guard as the tenant logout: the admin panel
 * shares the browser profile's offline store, and wiping it silently would
 * destroy money already collected.
 */
export const logoutAdmin = createAsyncThunk(
  'auth/logoutAdmin',
  async (_, { dispatch }) => {
    // Same IndexedDB guard as the tenant logout — see logout().
    let pending = 0;
    try {
      pending = await countUnsyncedPendingSales();
    } catch {
      pending = 0;
    }
    if (pending > 0) {
      const { default: i18n } = await import('@/i18n');
      const { isConfirmed } = await Swal.fire({
        title: i18n.t('Offline_logout_pending_title'),
        text: i18n.t('Offline_logout_pending_text', { count: pending }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: i18n.t('Offline_logout_confirm'),
        cancelButtonText: i18n.t('Offline_logout_cancel'),
        confirmButtonColor: '#d33',
      });
      if (!isConfirmed) return;
    }

    try {
      await api({ method: 'post', url: '/admin/auth/logout' });
    } catch {
      // Best-effort: continue clearing local state even if server call fails.
    } finally {
      // Same as the tenant logout: await the wipe so the delete is not aborted
      // by the caller's post-logout navigation.
      await clearPosOfflineData();
      try {
        // Mirror the tenant logout: drop both persisted stores so no admin
        // token / user PII is left behind in localStorage.
        localStorage.removeItem('persist:root');
        localStorage.removeItem('persist:auth-root');
      } catch {
        // ignore storage errors on logout
      }
      dispatch(clearAuthData());
    }
  }
);

export const refreshUserPermissions = createAsyncThunk<
  UserAuthData,
  void,
  { rejectValue: string | { reason: 'transient' } }
>(
  'auth/refreshUserPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const userAuth1 = await api.get('get_user_auth');
      return userAuth1.data.data as UserAuthData;
    } catch (err: unknown) {
      const status = rejectedStatus(err);
      // 401/403 is a definitive session invalidation; anything else (network,
      // timeout, 5xx) is transient and must NOT tear down the session.
      if (status === 401 || status === 403) {
        return rejectWithValue(`Session invalid (${status})`);
      }
      return rejectWithValue({ reason: 'transient' } as const);
    }
  }
);

export const checkAdminSession = createAsyncThunk<
  boolean,
  void,
  { rejectValue: boolean | { reason: 'transient' } }
>(
  'auth/checkAdminSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/auth/session');
      return response.data?.data?.valid === true && response.data?.data?.isSuperAdmin === true;
    } catch (err: unknown) {
      // 401/403 is the server's genuine "session invalid / not a super admin"
      // contract — keep the historical `false` payload so existing consumers
      // stay compatible. Anything else (network, timeout, 5xx) is transient:
      // tag it instead of swallowing it as an expiry, so callers can offer a
      // retry instead of wiping auth on a blip.
      const status = rejectedStatus(err);
      if (status === 401 || status === 403) {
        return rejectWithValue(false);
      }
      return rejectWithValue({ reason: 'transient' } as const);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSuperAdmin(state, action: PayloadAction<boolean>) {
      state.isSuperAdmin = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    SetDefaultLanguage(state, action: PayloadAction<string>) {
      // Note: In React, you'd integrate with i18next here or via middleware.
      state.defaultLanguage = action.payload;
    },
    setShowLanguage(state, action: PayloadAction<number | null>) {
      state.showLanguage = action.payload;
    },
    clearAuthData(state) {
      state.isAuthenticated = false;
      state.user = {};
      state.currentUser = {};
      state.Permissions = null;
      state.currentUserPermissions = null;
      state.allmodules = null;
      state.loading = false;
      state.error = null;
      state.isAdminLoggedIn = false;
      state.isSuperAdmin = false;
      state.tenantId = null;
      state.accessToken = null;

      // Side effects (tokenStorage.clearAuthData + disconnectEcho) moved to
      // the auth listener middleware — reducers must stay pure.
    },
    setTokens(state, action: PayloadAction<AccessData>) {
      const accessData = action.payload;
      // Token/tenant/expiry persistence to tokenStorage moved to the auth
      // listener middleware — reducers must stay pure.
      if (accessData.accessToken) {
        state.accessToken = accessData.accessToken;
      }
      if (accessData.tenantId) {
        state.tenantId = accessData.tenantId;
      }
      // Provisional 2FA credentials are not a session: establishing one here
      // (and the Echo bootstrap the listener middleware fires on this action)
      // would authenticate a user who has not completed the challenge.
      if (!accessData.twoFactorRequired) {
        state.isAuthenticated = true;
      }
      // initEcho moved to the auth listener middleware.
    }
  },
  extraReducers: (builder) => {
    // login
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.loading = false;
      const pendingTwoFactor = !!action.payload.twoFactorRequired;
      if (action.payload.accessToken) {
        state.accessToken = action.payload.accessToken;
        // tokenStorage persistence moved to the auth listener middleware
        // (reducers must stay pure). Pending 2FA keeps the provisional token
        // for the challenge request, but does NOT establish the session or
        // connect realtime yet.
        if (!pendingTwoFactor) {
          if (action.payload.tenantId) {
            state.tenantId = action.payload.tenantId;
          }
          state.isAuthenticated = true;
          // initEcho moved to the auth listener middleware (it skips this
          // action while twoFactorRequired is set).
        }
      }
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? 'Login failed';
    });

    // verifyTwoFactor
    builder.addCase(verifyTwoFactor.pending, (state) => {
      // Without this the challenge request shows no spinner and a previous
      // error banner stays up for the whole round trip.
      state.loading = true;
      state.error = null;
    });
    builder.addCase(verifyTwoFactor.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload.accessToken) {
        state.accessToken = action.payload.accessToken;
        if (action.payload.tenantId) {
          state.tenantId = action.payload.tenantId;
        }
        // tokenStorage persistence moved to the auth listener middleware.
      }
      state.isAuthenticated = true;
      // initEcho moved to the auth listener middleware.
    });
    builder.addCase(verifyTwoFactor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? 'Two-factor verification failed';
      // login.fulfilled persisted the provisional token so the challenge
      // request could be made. A failed challenge means no session was ever
      // established (tenantId was deliberately withheld too), so wipe the auth
      // storage instead of leaving a token for a half-authenticated session.
      // (The listener middleware performs the tokenStorage.clearAuthData().)
      state.accessToken = null;
    });

    // forgotPassword
    builder.addCase(forgotPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(forgotPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? 'Password reset request failed';
    });

    // resetPassword
    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? 'Password reset failed';
    });

    // refreshUserPermissions
    builder.addCase(refreshUserPermissions.fulfilled, (state, action) => {
      state.Permissions = action.payload.permissions;
      state.currentUserPermissions = action.payload.permissions;
      state.allmodules = action.payload.ModulesEnabled;
      state.user = action.payload.user;
      state.currentUser = action.payload.user;
      state.notifs = action.payload.notifs;
      state.showLanguage = action.payload.user.showLanguage ?? null;
      state.defaultLanguage = action.payload.user.defaultLanguage ?? 'en';
      state.isAuthenticated = true;
    });
    builder.addCase(refreshUserPermissions.rejected, (state, action) => {
      // Transient failures (network, timeouts, 5xx) reject with
      // `{ reason: 'transient' }` and must NOT wipe the session — clearing auth
      // on every refresh blip used to lock cashiers out. Only a definitive
      // session-invalid rejection (401/403 → string payload) resets auth.
      if (typeof action.payload !== 'string') return;
      state.Permissions = null;
      state.currentUserPermissions = null;
      state.allmodules = null;
      state.user = {};
      state.currentUser = {};
      state.notifs = null;
      state.showLanguage = null;
      state.defaultLanguage = 'en';
      state.isAuthenticated = false;
    });

    // checkAdminSession
    builder.addCase(checkAdminSession.fulfilled, (state, action) => {
      state.isAdminLoggedIn = action.payload;
      state.isSuperAdmin = action.payload;
    });
    builder.addCase(checkAdminSession.rejected, (state, action) => {
      // `false` = session genuinely invalid (401/403). `{ reason: 'transient' }`
      // = a network/5xx blip: don't flip admin flags on a blip or the shell
      // flickers the admin UI off while the request is merely retrying.
      if (action.payload === false) {
        state.isAdminLoggedIn = false;
        state.isSuperAdmin = false;
      }
    });
  }
});

export const { clearError, SetDefaultLanguage, setShowLanguage, clearAuthData, setTokens, setSuperAdmin } = authSlice.actions;

export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectCurrentUserPermissions = (state: { auth: AuthState }) => state.auth.Permissions;
export const selectAllModules = (state: { auth: AuthState }) => state.auth.allmodules;
export const selectNotifsAlert = (state: { auth: AuthState }) => state.auth.notifs;
export const selectDefaultLanguage = (state: { auth: AuthState }) => state.auth.defaultLanguage;
export const selectShowLanguage = (state: { auth: AuthState }) => state.auth.showLanguage;

export default authSlice.reducer;
